import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Clock, FileText, AlertCircle, Eye } from "lucide-react";
import { formatWithTimezoneLabel } from "@/lib/timezone";

interface SubmittedProposal {
  id: string;
  rfp_id: string;
  initial_bid_amount: number;
  submitted_at: string;
  selection_status: string;
  invitation_id: string;
  supplier_invitations: {
    invite_token: string;
    rfps: {
      title: string;
      description: string;
      submission_deadline: string;
      auction_start: string;
      auction_end: string;
      status: string;
      matter_name: string | null;
    };
  };
}

const SupplierSubmittedProposals = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<SubmittedProposal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchSubmittedProposals();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchSubmittedProposals = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    try {
      // First get the user's invitations
      const { data: invitations } = await supabase
        .from("supplier_invitations")
        .select("id, invite_token, rfp_id")
        .eq("supplier_email", user.email);

      if (!invitations || invitations.length === 0) {
        setProposals([]);
        setLoading(false);
        return;
      }

      const invitationIds = invitations.map(inv => inv.id);

      // Get all proposals for these invitations
      const { data: proposalData, error } = await supabase
        .from("proposals")
        .select(`
          id,
          rfp_id,
          initial_bid_amount,
          submitted_at,
          selection_status,
          invitation_id
        `)
        .in("invitation_id", invitationIds);

      if (error) throw error;

      // Fetch RFP data for each proposal
      const rfpIds = [...new Set((proposalData || []).map(p => p.rfp_id))];
      const { data: rfpData } = await supabase
        .from("rfps")
        .select("id, title, description, submission_deadline, auction_start, auction_end, status, matter_name")
        .in("id", rfpIds);

      // Combine the data
      const combined = (proposalData || []).map(proposal => {
        const invitation = invitations.find(inv => inv.id === proposal.invitation_id);
        const rfp = (rfpData || []).find(r => r.id === proposal.rfp_id);
        return {
          ...proposal,
          supplier_invitations: {
            invite_token: invitation?.invite_token || "",
            rfps: rfp || {
              title: "Unknown RFP",
              description: "",
              submission_deadline: "",
              auction_start: "",
              auction_end: "",
              status: "",
              matter_name: null
            }
          }
        };
      });

      setProposals(combined);
    } catch (err) {
      console.error("Error fetching submitted proposals:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string, selectionStatus: string) => {
    if (selectionStatus === "successful") {
      return <Badge className="bg-green-500">Awarded</Badge>;
    }
    if (selectionStatus === "unsuccessful") {
      return <Badge variant="destructive">Unsuccessful</Badge>;
    }
    
    switch (status) {
      case "AUCTION_ACTIVE":
        return <Badge className="bg-red-500 animate-pulse">Live Auction</Badge>;
      case "AUCTION_ENDED":
        return <Badge variant="secondary">Auction Ended</Badge>;
      case "COMPLETED":
        return <Badge className="bg-blue-500">Completed</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Submitted Proposals</h1>
          <p className="text-muted-foreground mt-2">All proposals you have submitted</p>
        </div>

        {proposals.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No submitted proposals yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {proposals.map((proposal) => (
              <Card key={proposal.id} className="hover:shadow-lg transition-shadow flex flex-col">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {proposal.supplier_invitations.rfps.matter_name || proposal.supplier_invitations.rfps.title}
                  </CardTitle>
                  {getStatusBadge(proposal.supplier_invitations.rfps.status, proposal.selection_status)}
                </CardHeader>
                <CardContent className="space-y-4 flex-1 flex flex-col">
                  <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                    {proposal.supplier_invitations.rfps.description}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Your Bid:</span>
                      <span className="font-semibold">${proposal.initial_bid_amount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>Submitted: {formatWithTimezoneLabel(proposal.submitted_at)}</span>
                    </div>
                  </div>
                  <div className="mt-auto pt-4 flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/supplier/rfp/${proposal.rfp_id}/view/${proposal.supplier_invitations.invite_token}`)}
                      className="flex-1"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View RFP
                    </Button>
                    {proposal.supplier_invitations.rfps.status === "AUCTION_ACTIVE" && (
                      <Button
                        onClick={() => navigate(`/rfp/${proposal.rfp_id}/bid/${proposal.supplier_invitations.invite_token}`)}
                        className="flex-1"
                      >
                        Join Auction
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierSubmittedProposals;
